import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { subtabAccessibleFor } from "../lib/modules";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Plane, Train, Bus, Car, CheckCircle2, XCircle, Ticket, Settings2, Eye, Trash2, Wallet, Search, PlaneTakeoff } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { PageHeader } from "../components/GradientCard";
import { toast } from "sonner";

const PURPOSES = ["Client Meeting", "Project Work", "Training", "Conference", "Internal Meeting", "Other"];
const TRAVEL_TYPES = ["One Way", "Round Trip", "Multi-City"];
const MODES = [{ k: "Flight", Icon: Plane }, { k: "Train", Icon: Train }, { k: "Bus", Icon: Bus }, { k: "Cab", Icon: Car }];
const ID_TYPES = ["Aadhaar Card", "Voter ID", "Passport", "Driving License", "PAN Card"];
const HOTEL_CATEGORIES = ["1 Star", "2 Star", "3 Star", "4 Star", "5 Star", "Budget Hotel", "Service Apartment"];
const STATUS_COLOR = {
  Submitted: "bg-amber-100 text-amber-800",
  Approved: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-rose-100 text-rose-700",
  Booked: "bg-blue-100 text-blue-800",
};

const REIMB_COLOR = {
  "Pending Decision": "bg-amber-100 text-amber-800",
  "Awaiting Ledger Entry": "bg-indigo-100 text-indigo-800",
  "Awaiting Payment": "bg-orange-100 text-orange-800",
  "Paid": "bg-emerald-100 text-emerald-800",
  "Not Required": "bg-slate-200 text-slate-700",
};

const REIMB_FILTERS = ["All", "Pending Decision", "Awaiting Ledger Entry", "Awaiting Payment", "Paid", "Not Required"];
const STATUS_FILTERS_DEFAULT = ["All", "Submitted", "Approved", "Rejected", "Booked"];

function tbTotal(b) {
  const ovr = b?.total_amount_override;
  if (ovr !== "" && ovr !== undefined && ovr !== null && !Number.isNaN(Number(ovr))) return Number(ovr);
  return (Number(b?.actual_cost) || 0) + (Number(b?.hotel_actual_cost) || 0);
}

function matchesFilter(b, q, status) {
  if (status && status !== "All" && b.status !== status) return false;
  if (!q) return true;
  const hay = [
    b.created_by_name, b.created_by_department, b.booker_employee_id,
    b.booker_email, b.purpose, b.purpose_other, b.travel_type, b.mode_of_travel,
    b.from_city, b.to_city, (b.cities || []).join(" "),
    b.traveler_full_name, b.assigned_to_name, b.pnr, b.vendor, b.booking_reference,
    b.paid_by, b.paid_by_employee_name, b.reimbursement_reference_number,
  ].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q.toLowerCase());
}

function bookerFromUser(user) {
  return {
    booker_employee_id: user?.employee_code || "",
    booker_name: user?.name || "",
    booker_department: user?.department || "",
    booker_designation: user?.designation || "",
    booker_mobile: user?.phone || user?.company_phone || user?.personal_whatsapp || user?.whatsapp || "",
    booker_email: user?.email || "",
  };
}

function emptyForm(user) {
  return {
    ...bookerFromUser(user),
    travel_for_self: true,
    traveler_full_name: user?.name || "",
    traveler_gender: user?.gender || "",
    traveler_dob: user?.date_of_birth || "",
    traveler_age: "",
    traveler_nationality: "Indian",
    traveler_govt_id_type: "Aadhaar Card",
    traveler_govt_id_number: user?.aadhaar_number || "",
    purpose: "",
    purpose_other: "",
    travel_type: "One Way",
    mode_of_travel: "Flight",
    from_city: "", to_city: "", cities: [],
    preferred_travel_date: "", preferred_departure_time: "", flexible_outbound: false,
    return_date: "", preferred_return_time: "", flexible_return: false,
    hotel_required: false, hotel_checkin: "", hotel_checkout: "", hotel_no_of_days: "",
    hotel_category: "", hotel_special_requirements: "",
    approx_cost_travel: "", approx_cost_hotel: "",
    assigned_to_id: "", assigned_to_name: "",
  };
}

function calcAge(dob) {
  // v109.9.154 — Robust age calculation. Accepts "YYYY-MM-DD" (native
  // <input type="date">) and "DD-MM-YYYY" (legacy stored format).
  // Returns "" for empty/invalid input, integer years otherwise.
  if (!dob) return "";
  let s = String(dob).trim();
  if (!s) return "";
  // Coerce "DD-MM-YYYY" or "DD/MM/YYYY" → "YYYY-MM-DD"
  const dmy = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmy) s = `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 150 ? age : "";
}

function daysBetween(a, b) {
  if (!a || !b) return "";
  const d1 = new Date(a), d2 = new Date(b);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return "";
  return Math.max(0, Math.round((d2 - d1) / 86400000));
}

export default function TravelBooking({ embedded = false, actionPulse = null } = {}) {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("requests");
  const [access, setAccess] = useState({ i_can_approve: false, i_can_book: false, i_can_reimburse: false, is_master: false, approval_user_ids: [], ticket_user_ids: [] });
  const [people, setPeople] = useState([]);
  const [mine, setMine] = useState([]);
  const [approval, setApproval] = useState([]);
  const [booking, setBooking] = useState([]);
  const [reimb, setReimb] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(() => emptyForm(user));
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  // v109.9.40 — Tab Access dialog removed; stub retained to avoid rippling changes.
  const tabAccessOpen = false; const setTabAccessOpen = () => {};
  void tabAccessOpen; void setTabAccessOpen;

  const loadAccess = async () => { try { const { data } = await api.get("/travel-bookings/tab-access"); setAccess(data); } catch { /* */ } };
  const loadMine = async () => { try { const { data } = await api.get("/travel-bookings?scope=mine"); setMine(data || []); } catch { /* */ } };
  const loadApproval = async () => { if (!access.i_can_approve) return; try { const { data } = await api.get("/travel-bookings?scope=approval"); setApproval(data || []); } catch { /* */ } };
  const loadBooking = async () => { if (!access.i_can_book) return; try { const { data } = await api.get("/travel-bookings?scope=booking"); setBooking(data || []); } catch { /* */ } };
  const loadReimb = async () => { if (!access.i_can_reimburse) return; try { const { data } = await api.get("/travel-bookings?scope=reimbursement"); setReimb(data || []); } catch { /* */ } };

  useEffect(() => { loadAccess(); api.get("/directory").then(({ data }) => setPeople(data || [])).catch(() => {}); }, []); // eslint-disable-line
  useEffect(() => {
    if (tab === "requests") loadMine();
    if (tab === "approval") loadApproval();
    if (tab === "booking") loadBooking();
    if (tab === "reimbursement") loadReimb();
    setFilterText(""); setFilterStatus("All");
  }, [tab, access.i_can_approve, access.i_can_book, access.i_can_reimburse]); // eslint-disable-line

  const filteredMine = useMemo(() => mine.filter((b) => matchesFilter(b, filterText, filterStatus)), [mine, filterText, filterStatus]);
  const filteredApproval = useMemo(() => approval.filter((b) => matchesFilter(b, filterText, filterStatus)), [approval, filterText, filterStatus]);
  const filteredBooking = useMemo(() => booking.filter((b) => matchesFilter(b, filterText, filterStatus)), [booking, filterText, filterStatus]);
  const filteredReimb = useMemo(() => reimb.filter((b) => {
    if (!matchesFilter(b, filterText, null)) return false;
    if (filterStatus && filterStatus !== "All" && (b.reimbursement_status || "Pending Decision") !== filterStatus) return false;
    return true;
  }), [reimb, filterText, filterStatus]);

  // Backfill the "Filled By" booker fields the moment the auth context resolves
  // (component may mount before /auth/me returns), and refresh whenever the
  // logged-in user record changes.
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({ ...f, ...bookerFromUser(user) }));
  }, [user?.id, user?.employee_code, user?.name, user?.department, user?.designation, user?.phone, user?.company_phone, user?.personal_whatsapp, user?.whatsapp, user?.email]); // eslint-disable-line

  const age = useMemo(() => calcAge(form.traveler_dob), [form.traveler_dob]);
  const hotelDays = useMemo(() => daysBetween(form.hotel_checkin, form.hotel_checkout), [form.hotel_checkin, form.hotel_checkout]);
  // v109.9.154 — Store age as STRING to keep the disabled <Input> stable
  // (React inputs prefer string values) and compare via String() to sidestep
  // "0 !== '0'" edge cases when the DOB is today's date.
  useEffect(() => {
    const s = age === "" ? "" : String(age);
    setForm((f) => (String(f.traveler_age ?? "") === s ? f : { ...f, traveler_age: s }));
  }, [age]);
  useEffect(() => { if (hotelDays !== form.hotel_no_of_days) setForm((f) => ({ ...f, hotel_no_of_days: hotelDays })); }, [hotelDays]); // eslint-disable-line

  const flipSelf = (isSelf) => {
    setForm((f) => ({
      ...f,
      travel_for_self: isSelf,
      traveler_full_name: isSelf ? (user?.name || "") : "",
      traveler_gender: isSelf ? (user?.gender || "") : "",
      traveler_dob: isSelf ? (user?.date_of_birth || "") : "",
      traveler_govt_id_type: "Aadhaar Card",
      traveler_govt_id_number: isSelf ? (user?.aadhaar_number || "") : "",
    }));
  };

  // React to Dashboard "Book Travel" quick-action — auto-open the New Booking form.
  // v109.9.156 — depend on the pulse's `n` timestamp only. Depending on the
  // whole `actionPulse` object re-fired the effect on EVERY parent render,
  // which is why the form reopened as soon as the user closed it.
  useEffect(() => {
    if (actionPulse && actionPulse.key === "new") {
      openNewBooking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionPulse?.key, actionPulse?.n]);

  const openNewBooking = async () => {
    setEditing(null);
    setOpenCreate(true);
    // Pull the freshest user record so newly-saved profile data (mobile,
    // designation, employee_code, etc.) is reflected in the Filled By section.
    let fresh = user;
    try {
      const { data } = await api.get("/auth/me");
      fresh = data;
      refresh?.(); // also update the global auth context (fire-and-forget)
    } catch { /* fall back to context */ }
    setForm(emptyForm(fresh));
  };

  const submit = async () => {
    if (!form.purpose) { toast.error("Pick a purpose"); return; }
    if (!form.preferred_travel_date) { toast.error("Travel date is required"); return; }
    if (form.travel_type === "Round Trip" && !form.return_date) { toast.error("Return date is required for Round Trip"); return; }
    if (form.travel_type !== "Multi-City" && (!form.from_city || !form.to_city)) { toast.error("From & To cities are required"); return; }
    if (form.travel_type === "Multi-City" && (form.cities || []).length < 2) { toast.error("Multi-City needs at least 2 cities"); return; }
    try {
      if (editing) {
        await api.patch(`/travel-bookings/${editing.id}`, form);
        toast.success("Booking updated");
      } else {
        await api.post("/travel-bookings", form);
        toast.success("Travel booking submitted");
      }
      setOpenCreate(false); setEditing(null); setForm(emptyForm(user)); loadMine();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openEditMine = (b) => { setEditing(b); setForm({ ...emptyForm(user), ...b }); setOpenCreate(true); };

  // v109.9.115 — Approval action moved into a proper dialog (mandatory
  // reason on reject) and reached via the ViewDialog only. Row-level
  // Approve/Reject buttons removed in the approval sub-tab.
  const [actionDialog, setActionDialog] = useState(null); // { booking, action }
  const [actionRemark, setActionRemark] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const submitAction = async () => {
    if (!actionDialog) return;
    if (actionDialog.action === "reject" && !actionRemark.trim()) {
      toast.error("Rejection reason is mandatory");
      return;
    }
    setActionBusy(true);
    try {
      const ok = actionDialog.action === "approve";
      await api.post(`/travel-bookings/${actionDialog.booking.id}/${ok ? "approve" : "reject"}`, { remark: actionRemark.trim() });
      toast.success(ok ? "Approved" : "Rejected");
      setActionDialog(null); setActionRemark(""); setViewing(null);
      loadApproval(); loadBooking();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setActionBusy(false); }
  };

  return (
    <div className={embedded ? "space-y-4" : "space-y-6"}>
      {!embedded ? (
      <PageHeader
        moduleKey="travel"
        tone="blue"
        icon={PlaneTakeoff}
        eyebrow="Travel"
        title="Travel Booking"
        subtitle="Submit travel requests, get approval, and capture ticket details."
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={openNewBooking} className="bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white shadow-sm" data-testid="tb-new">
              <Plus size={14} className="mr-1.5" /> New Booking
            </Button>
          </div>
        }
      />
      ) : (
        <div className="flex items-center justify-end gap-2">
          <Button onClick={openNewBooking} className="bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white shadow-sm" data-testid="tb-new">
            <Plus size={14} className="mr-1.5" /> New Booking
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {subtabAccessibleFor(user, "travel", "booking", "requests") && (
            <TabsTrigger value="requests" data-testid="tb-tab-requests">Travel Booking Request ({mine.length})</TabsTrigger>
          )}
          {access.i_can_approve && subtabAccessibleFor(user, "travel", "booking", "approval") && <TabsTrigger value="approval" data-testid="tb-tab-approval">Approval ({approval.length})</TabsTrigger>}
          {access.i_can_book && subtabAccessibleFor(user, "travel", "booking", "booking") && <TabsTrigger value="booking" data-testid="tb-tab-booking">Ticket Booking Details ({booking.length})</TabsTrigger>}
          {access.i_can_reimburse && subtabAccessibleFor(user, "travel", "booking", "reimbursement") && <TabsTrigger value="reimbursement" data-testid="tb-tab-reimbursement">Reimbursement ({reimb.length})</TabsTrigger>}
        </TabsList>

        <FilterBar
          q={filterText} setQ={setFilterText}
          status={filterStatus} setStatus={setFilterStatus}
          options={tab === "reimbursement" ? REIMB_FILTERS : STATUS_FILTERS_DEFAULT}
          label={tab === "reimbursement" ? "Reimbursement Status" : "Status"}
        />

        <TabsContent value="requests" className="mt-4">
          <BookingsTable rows={filteredMine} emptyText="No travel requests yet." onView={setViewing} onEdit={openEditMine} canEdit={(b) => b.status === "Rejected" || b.status === "Submitted"} />
        </TabsContent>
        {access.i_can_approve && (
          <TabsContent value="approval" className="mt-4">
            <BookingsTable
              rows={filteredApproval}
              emptyText="No pending approvals."
              onView={setViewing}
              actionMode
              onAction={(b) => setViewing({ ...b, __for_approval: true })}
            />
          </TabsContent>
        )}
        {access.i_can_book && (
          <TabsContent value="booking" className="mt-4">
            <BookingsTable rows={filteredBooking} emptyText="No approved bookings to ticket yet." onView={setViewing} onTicket={setViewing} />
          </TabsContent>
        )}
        {access.i_can_reimburse && (
          <TabsContent value="reimbursement" className="mt-4">
            <ReimbursementTable rows={filteredReimb} access={access} userRole={user?.role} onView={setViewing} onChanged={loadReimb} />
          </TabsContent>
        )}
      </Tabs>

      <CreateDialog open={openCreate} setOpen={setOpenCreate} form={form} setForm={setForm} flipSelf={flipSelf} submit={submit} editing={editing} people={people} />
      <ViewDialog
        viewing={viewing}
        setViewing={setViewing}
        access={access}
        onChanged={() => { loadApproval(); loadBooking(); loadMine(); loadReimb(); }}
        onApprove={(b) => { setActionDialog({ booking: b, action: "approve" }); setActionRemark(""); }}
        onReject={(b) => { setActionDialog({ booking: b, action: "reject" }); setActionRemark(""); }}
      />

      {/* v109.9.115 — Booking approve / reject dialog with mandatory reason on reject. */}
      <Dialog open={!!actionDialog} onOpenChange={(v) => { if (!v) { setActionDialog(null); setActionRemark(""); } }}>
        <DialogContent className="max-w-md" data-testid="tb-action-dialog">
          <DialogHeader>
            <DialogTitle className="heading">
              {actionDialog?.action === "approve" ? "Approve travel booking" : "Reject travel booking — reason required"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[12px] text-slate-600 dark:text-slate-300">
              <b>{actionDialog?.booking?.traveler_name}</b> · {actionDialog?.booking?.travel_type} · {actionDialog?.booking?.mode_of_travel}
              <div className="text-[10.5px] text-slate-500 mt-0.5">{actionDialog?.booking?.from_city || (actionDialog?.booking?.cities?.[0])} → {actionDialog?.booking?.to_city || (actionDialog?.booking?.cities?.slice(-1)?.[0])}</div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                Remark {actionDialog?.action === "reject" ? "*" : "(optional)"}
              </Label>
              <Textarea
                rows={3}
                value={actionRemark}
                onChange={(e) => setActionRemark(e.target.value)}
                placeholder={actionDialog?.action === "reject" ? "Why is this being rejected? (mandatory)" : "Optional note for the traveller / booking team"}
                className="mt-1.5"
                data-testid="tb-action-remark"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setActionDialog(null); setActionRemark(""); }} disabled={actionBusy}>Cancel</Button>
            <Button
              onClick={submitAction}
              disabled={actionBusy}
              className={actionDialog?.action === "reject" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              data-testid="tb-action-submit"
            >
              {actionBusy ? "Working…" : (actionDialog?.action === "approve" ? "Confirm Approval" : "Confirm Rejection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* v109.9.40 — TabAccessDialog removed per user request */}
    </div>
  );
}

function FilterBar({ q, setQ, status, setStatus, options, label }) {
  return (
    <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="tb-filterbar">
      <div className="relative flex-1 min-w-[220px] max-w-md">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee, vendor, PNR, route, payer…" className="pl-7 h-9 text-[12px]" data-testid="tb-filter-search" />
      </div>
      <div className="flex items-center gap-1.5 text-[11px]">
        <Label className="uppercase tracking-wider font-semibold text-slate-500">{label}</Label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 border border-slate-200 rounded-md px-2 text-[12px] bg-white" data-testid="tb-filter-status">
          {options.map((o) => <option key={o}>{o}</option>)}
        </select>
        {(q || (status && status !== "All")) && (
          <Button variant="outline" onClick={() => { setQ(""); setStatus("All"); }} className="h-9 text-[11px]" data-testid="tb-filter-clear">Clear</Button>
        )}
      </div>
    </div>
  );
}

function paymentPayerLabel(paidBy) {
  switch (paidBy) {
    case "Company": return "Company Payer / Cost Centre";
    case "Employee (Reimbursement)": return "Employee Who Paid";
    case "Client": return "Client Name";
    case "Vendor": return "Vendor Name";
    case "Other": return "Payer Name / Description";
    default: return "Payer Name";
  }
}
function paymentPayerPlaceholder(paidBy) {
  switch (paidBy) {
    case "Company": return "e.g. Finance · Corporate Card · Cost Centre TR-01";
    case "Employee (Reimbursement)": return "Type the employee's full name";
    case "Client": return "Client / Project name";
    case "Vendor": return "Vendor / Agency name";
    case "Other": return "Who paid (with brief context)";
    default: return "";
  }
}

function ReimbursementTable({ rows, access, userRole, onView, onChanged }) {
  // v109.9.117 — Every reimbursement action now goes through a real dialog
  // with a mandatory remark + any required action-specific fields.
  const [actionDialog, setActionDialog] = useState(null); // { row, action }
  const [form, setForm] = useState({ remark: "", mode: "NEFT", reference: "" });
  const [busy, setBusy] = useState(false);

  const openAction = (row, action) => {
    setActionDialog({ row, action });
    setForm({ remark: "", mode: "NEFT", reference: "" });
  };

  const submitAction = async () => {
    if (!actionDialog) return;
    const { row, action } = actionDialog;
    if (!form.remark.trim()) { toast.error("Remark is mandatory for every action"); return; }
    setBusy(true);
    try {
      if (action === "require_yes" || action === "require_no") {
        await api.post(`/travel-bookings/${row.id}/reimbursement-required`, { required: action === "require_yes", remark: form.remark.trim() });
        toast.success(action === "require_yes" ? "Reimbursement marked Required" : "Marked Not Required");
      } else if (action === "post_ledger") {
        await api.post(`/travel-bookings/${row.id}/reimbursement-ledger`, { remark: form.remark.trim() });
        toast.success("Ledger entry posted");
      } else if (action === "mark_paid") {
        if (!["NEFT", "RTGS", "UPI"].includes(form.mode)) { toast.error("Mode must be NEFT / RTGS / UPI"); setBusy(false); return; }
        if (!form.reference.trim()) { toast.error("Reference number is required"); setBusy(false); return; }
        await api.post(`/travel-bookings/${row.id}/reimbursement-paid`, {
          payment_mode: form.mode,
          reference_number: form.reference.trim(),
          remark: form.remark.trim(),
        });
        toast.success("Reimbursement marked Paid");
      }
      setActionDialog(null);
      onChanged?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const isMaster = access.is_master;
  const isAccountsOrMaster = isMaster || userRole === "accounts_admin";
  return (
    <div className="pk-card overflow-x-auto" data-testid="tb-reimb-table">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 bg-slate-50/60">
          <tr>
            <Th>Traveler / Booker</Th>
            <Th>Paid By (Employee)</Th>
            <Th>Total Amount</Th>
            <Th>Reimbursement Status</Th>
            <Th>Payment</Th>
            <Th className="text-right">Action</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-400 text-sm">No reimbursement-pending tickets.</td></tr>}
          {rows.map((b) => {
            const st = b.reimbursement_status || "Pending Decision";
            return (
              <tr key={b.id} className="border-b border-slate-100" data-testid={`tb-reimb-row-${b.id}`} data-highlight-id={b.id}>
                <td className="px-4 py-3" data-label="Traveler">
                  <div className="font-semibold">{b.traveler_full_name || b.created_by_name}</div>
                  <div className="text-[10.5px] text-slate-500">Booker: {b.created_by_name} · {b.created_by_department}</div>
                  <div className="text-[10.5px] text-slate-500">{b.pnr ? `PNR ${b.pnr}` : ""}{b.vendor ? ` · ${b.vendor}` : ""}</div>
                </td>
                <td className="px-4 py-3 text-[12px]" data-label="Paid By">
                  <div className="font-semibold">{b.paid_by_employee_name || "—"}</div>
                  <div className="text-[10.5px] text-slate-500">Employee (Reimbursement)</div>
                </td>
                <td className="px-4 py-3 mono text-[12px]" data-label="Total">
                  ₹{tbTotal(b)}
                  {b.total_amount_override !== "" && b.total_amount_override !== undefined && b.total_amount_override !== null && (
                    <div className="text-[10px] text-slate-500">(override)</div>
                  )}
                </td>
                <td className="px-4 py-3" data-label="Status">
                  <Badge className={REIMB_COLOR[st] || "bg-slate-100"}>{st}</Badge>
                  {b.reimbursement_required === true && !b.ledger_entry_done && b.reimbursement_required_remark && <div className="text-[10.5px] text-slate-500 mt-1">{b.reimbursement_required_remark}</div>}
                </td>
                <td className="px-4 py-3 text-[12px]" data-label="Payment">
                  {b.reimbursement_paid ? (
                    <>
                      <div><b>{b.reimbursement_payment_mode}</b> · <span className="mono">{b.reimbursement_reference_number}</span></div>
                      <div className="text-[10.5px] text-slate-500">{b.reimbursement_paid_by_name}{b.reimbursement_paid_at ? ` · ${b.reimbursement_paid_at.slice(0, 10)}` : ""}</div>
                    </>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap" data-label="Action">
                  <Button size="sm" variant="outline" onClick={() => onView?.(b)} className="h-8 text-[11px] mr-1" data-testid={`tb-reimb-view-${b.id}`}><Eye size={11} className="mr-1" /> View All Details</Button>
                  {st === "Pending Decision" && (
                    <>
                      <Button size="sm" onClick={() => openAction(b, "require_yes")} className="h-8 text-[11px] bg-emerald-600 text-white mr-1" data-testid={`tb-reimb-yes-${b.id}`}>Reimburse Yes</Button>
                      <Button size="sm" onClick={() => openAction(b, "require_no")} className="h-8 text-[11px] bg-slate-600 text-white" data-testid={`tb-reimb-no-${b.id}`}>Not Required</Button>
                    </>
                  )}
                  {st === "Awaiting Ledger Entry" && isAccountsOrMaster && (
                    <Button size="sm" onClick={() => openAction(b, "post_ledger")} className="h-8 text-[11px] bg-indigo-600 text-white" data-testid={`tb-reimb-ledger-${b.id}`}>Post Ledger Entry</Button>
                  )}
                  {st === "Awaiting Payment" && isMaster && (
                    <Button size="sm" onClick={() => openAction(b, "mark_paid")} className="h-8 text-[11px] bg-blue-600 text-white" data-testid={`tb-reimb-pay-${b.id}`}><Wallet size={11} className="mr-1" /> Mark Paid</Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* v109.9.117 — Reimbursement action dialog. Mandatory remark for every
          action; Mark Paid additionally requires payment mode + reference. */}
      <Dialog open={!!actionDialog} onOpenChange={(v) => { if (!v) setActionDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="tb-reimb-action-dialog">
          <DialogHeader>
            <DialogTitle className="heading">
              {actionDialog?.action === "require_yes" && "Reimburse — Yes"}
              {actionDialog?.action === "require_no" && "Reimburse — Not Required"}
              {actionDialog?.action === "post_ledger" && "Post Ledger Entry"}
              {actionDialog?.action === "mark_paid" && "Mark Reimbursement Paid"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[12px] text-slate-600 dark:text-slate-300">
              <b>{actionDialog?.row?.traveler_full_name || actionDialog?.row?.created_by_name}</b>
              <span className="ml-2 mono">₹{tbTotal(actionDialog?.row || {})}</span>
              {actionDialog?.row?.paid_by_employee_name && <span className="ml-2 text-slate-500">· Paid by {actionDialog.row.paid_by_employee_name}</span>}
            </div>

            {actionDialog?.action === "mark_paid" && (
              <>
                <F label="Payment Mode *">
                  <select
                    value={form.mode}
                    onChange={(e) => setForm({ ...form, mode: e.target.value })}
                    className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white"
                    data-testid="tb-reimb-action-mode"
                  >
                    <option>NEFT</option>
                    <option>RTGS</option>
                    <option>UPI</option>
                  </select>
                </F>
                <F label={`${form.mode} Reference Number *`}>
                  <Input
                    value={form.reference}
                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                    placeholder="Txn ID / UTR"
                    data-testid="tb-reimb-action-ref"
                  />
                </F>
              </>
            )}

            <F label="Remark *" full>
              <Textarea
                rows={3}
                value={form.remark}
                onChange={(e) => setForm({ ...form, remark: e.target.value })}
                placeholder="This remark is mandatory and stored on the audit trail."
                data-testid="tb-reimb-action-remark"
              />
              <div className="text-[10.5px] text-slate-500 mt-1">
                Every reimbursement action requires an audit remark — no exceptions.
              </div>
            </F>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={submitAction}
              disabled={busy}
              className={
                actionDialog?.action === "require_no" ? "bg-slate-600 hover:bg-slate-700 text-white" :
                actionDialog?.action === "mark_paid" ? "bg-blue-600 hover:bg-blue-700 text-white" :
                actionDialog?.action === "post_ledger" ? "bg-indigo-600 hover:bg-indigo-700 text-white" :
                "bg-emerald-600 hover:bg-emerald-700 text-white"
              }
              data-testid="tb-reimb-action-submit"
            >
              {busy ? "Working…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingsTable({ rows, emptyText, onView, onEdit, onApprove, onReject, onTicket, canEdit, actionMode, onAction }) {
  const [viewMode] = useGlobalViewMode();
  if (rows.length === 0) return <div className="pk-card p-12 text-center text-slate-400 text-sm">{emptyText}</div>;
  if (viewMode === "card") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="tb-cards">
        {rows.map((b) => (
          <div key={b.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`tb-card-${b.id}`} data-highlight-id={b.id}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-900 truncate">{b.created_by_name}</div>
                <div className="text-[10.5px] text-slate-500 truncate">{b.created_by_department}</div>
              </div>
              <Badge className={(STATUS_COLOR[b.status] || "bg-slate-100") + " text-[10px] shrink-0"}>{b.status}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
              <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Purpose</div><div className="text-slate-800 truncate">{b.purpose}{b.purpose === "Other" && b.purpose_other ? ` · ${b.purpose_other}` : ""}</div></div>
              <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Travel</div><div className="text-slate-800 truncate">{b.travel_type} · {b.mode_of_travel}</div><div className="text-[10.5px] text-slate-500 truncate">{b.from_city || (b.cities?.[0])} → {b.to_city || (b.cities?.[b.cities?.length - 1])}</div></div>
              <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Date</div><div className="mono text-slate-800">{b.preferred_travel_date}</div></div>
            </div>
            <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-1.5">
              {actionMode ? (
                <Button size="sm" onClick={() => onAction?.(b)} className="h-7 text-[10.5px] bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`tb-action-card-${b.id}`}>
                  <Eye size={10} className="mr-1" /> Take Action
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={() => onView?.(b)} className="h-7 text-[10.5px]" data-testid={`tb-view-card-${b.id}`}><Eye size={10} className="mr-1" /> View</Button>
                  {onEdit && canEdit?.(b) && <Button size="sm" variant="outline" onClick={() => onEdit(b)} className="h-7 text-[10.5px]" data-testid={`tb-edit-card-${b.id}`}>Edit</Button>}
                  {onApprove && <Button size="sm" onClick={() => onApprove(b)} className="h-7 text-[10.5px] bg-emerald-600 text-white" data-testid={`tb-approve-card-${b.id}`}><CheckCircle2 size={10} className="mr-1" /> Approve</Button>}
                  {onReject && <Button size="sm" onClick={() => onReject(b)} className="h-7 text-[10.5px] bg-rose-600 text-white" data-testid={`tb-reject-card-${b.id}`}><XCircle size={10} className="mr-1" /> Reject</Button>}
                  {onTicket && b.status === "Approved" && <Button size="sm" onClick={() => onTicket(b)} className="h-7 text-[10.5px] bg-blue-600 text-white" data-testid={`tb-ticket-card-${b.id}`}><Ticket size={10} className="mr-1" /> Book Ticket</Button>}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="pk-card overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 bg-slate-50/60">
          <tr><Th>Employee</Th><Th>Purpose</Th><Th>Travel</Th><Th>Date</Th><Th>Status</Th><Th className="text-right">Action</Th></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-400 text-sm">{emptyText}</td></tr>}
          {rows.map((b) => (
            <tr key={b.id} className="border-b border-slate-100" data-testid={`tb-row-${b.id}`} data-highlight-id={b.id}>
              <td className="px-4 py-3" data-label="Employee"><div className="font-semibold">{b.created_by_name}</div><div className="text-[10.5px] text-slate-500">{b.created_by_department}</div></td>
              <td className="px-4 py-3 text-[12px]" data-label="Purpose">{b.purpose}{b.purpose === "Other" && b.purpose_other ? ` · ${b.purpose_other}` : ""}</td>
              <td className="px-4 py-3 text-[12px]" data-label="Travel">{b.travel_type} · {b.mode_of_travel}<div className="text-[10.5px] text-slate-500">{b.from_city || (b.cities?.[0])} → {b.to_city || (b.cities?.[b.cities?.length - 1])}</div></td>
              <td className="px-4 py-3 mono text-[12px]" data-label="Date">{b.preferred_travel_date}</td>
              <td className="px-4 py-3" data-label="Status"><Badge className={STATUS_COLOR[b.status] || "bg-slate-100"}>{b.status}</Badge></td>
              <td className="px-4 py-3 text-right" data-label="Action">
                {actionMode ? (
                  <Button size="sm" onClick={() => onAction?.(b)} className="h-8 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`tb-action-${b.id}`}>
                    <Eye size={11} className="mr-1" /> Take Action
                  </Button>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => onView?.(b)} className="h-8 text-[11px] mr-1" data-testid={`tb-view-${b.id}`}><Eye size={11} className="mr-1" /> View</Button>
                    {onEdit && canEdit?.(b) && <Button size="sm" variant="outline" onClick={() => onEdit(b)} className="h-8 text-[11px] mr-1" data-testid={`tb-edit-${b.id}`}>Edit</Button>}
                    {onApprove && <Button size="sm" onClick={() => onApprove(b)} className="h-8 text-[11px] bg-emerald-600 text-white mr-1" data-testid={`tb-approve-${b.id}`}><CheckCircle2 size={11} className="mr-1" /> Approve</Button>}
                    {onReject && <Button size="sm" onClick={() => onReject(b)} className="h-8 text-[11px] bg-rose-600 text-white" data-testid={`tb-reject-${b.id}`}><XCircle size={11} className="mr-1" /> Reject</Button>}
                    {onTicket && b.status === "Approved" && <Button size="sm" onClick={() => onTicket(b)} className="h-8 text-[11px] bg-blue-600 text-white" data-testid={`tb-ticket-${b.id}`}><Ticket size={11} className="mr-1" /> Book Ticket</Button>}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
const Th = ({ children, className = "" }) => <th className={`px-4 py-3 text-left pk-table-header ${className}`}>{children}</th>;
const F = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

function CreateDialog({ open, setOpen, form, setForm, flipSelf, submit, editing, people }) {
  const isMulti = form.travel_type === "Multi-City";
  const isRound = form.travel_type === "Round Trip";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="tb-create-dialog">
        <DialogHeader>
          <DialogTitle className="heading">{editing ? "Edit" : "New"} Travel Booking</DialogTitle>
        </DialogHeader>

        <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40 space-y-1.5 text-[12px]" data-testid="tb-booker">
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700">Filled By</div>
          <div className="grid grid-cols-2 gap-2">
            <div><b>Name:</b> {form.booker_name || "—"}</div>
            <div><b>Employee ID:</b> {form.booker_employee_id || "—"}</div>
            <div><b>Department:</b> {form.booker_department || "—"}</div>
            <div><b>Designation:</b> {form.booker_designation || "—"}</div>
            <div><b>Mobile:</b> {form.booker_mobile || "—"}</div>
            <div><b>Email:</b> {form.booker_email || "—"}</div>
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <Button onClick={() => flipSelf(true)} className={`flex-1 ${form.travel_for_self ? "bg-emerald-600 text-white" : "bg-white border border-slate-300 text-slate-700"}`} data-testid="tb-self">For Myself</Button>
          <Button onClick={() => flipSelf(false)} className={`flex-1 ${!form.travel_for_self ? "bg-emerald-600 text-white" : "bg-white border border-slate-300 text-slate-700"}`} data-testid="tb-other">For Someone Else</Button>
        </div>

        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700 mt-4 mb-1">Traveler Information</div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Full Name (as per Govt ID) *"><Input value={form.traveler_full_name} onChange={(e) => setForm({ ...form, traveler_full_name: e.target.value })} data-testid="tb-tr-name" /></F>
          <F label="Gender *">
            <select value={form.traveler_gender} onChange={(e) => setForm({ ...form, traveler_gender: e.target.value })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="tb-tr-gender">
              <option value="">—</option><option>Male</option><option>Female</option><option>Other</option>
            </select>
          </F>
          <F label="Date of Birth *"><Input type="date" value={form.traveler_dob} onChange={(e) => setForm((f) => ({ ...f, traveler_dob: e.target.value }))} data-testid="tb-tr-dob" /></F>
          <F label="Age"><Input value={form.traveler_age} disabled className="bg-slate-50" data-testid="tb-tr-age" /></F>
          <F label="Nationality *"><Input value={form.traveler_nationality} onChange={(e) => setForm({ ...form, traveler_nationality: e.target.value })} data-testid="tb-tr-nationality" /></F>
          {!form.travel_for_self && (
            <F label="Govt ID Type *">
              <select value={form.traveler_govt_id_type} onChange={(e) => setForm({ ...form, traveler_govt_id_type: e.target.value })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="tb-tr-idtype">
                {ID_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </F>
          )}
          <F label="Govt ID Number *"><Input value={form.traveler_govt_id_number} onChange={(e) => setForm({ ...form, traveler_govt_id_number: e.target.value })} className="mono" data-testid="tb-tr-idno" /></F>
        </div>

        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700 mt-4 mb-1">Trip</div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Purpose of Travel *">
            <select value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="tb-purpose">
              <option value="">—</option>{PURPOSES.map((p) => <option key={p}>{p}</option>)}
            </select>
          </F>
          {form.purpose === "Other" && <F label="Other Purpose *"><Input value={form.purpose_other} onChange={(e) => setForm({ ...form, purpose_other: e.target.value })} data-testid="tb-purpose-other" /></F>}
          <F label="Travel Type *">
            <select value={form.travel_type} onChange={(e) => setForm({ ...form, travel_type: e.target.value })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="tb-traveltype">
              {TRAVEL_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </F>
          <F label="Mode of Travel *" full>
            <div className="flex gap-2 flex-wrap">
              {MODES.map(({ k, Icon }) => {
                const active = form.mode_of_travel === k;
                return (
                  <Button
                    key={k}
                    type="button"
                    variant="outline"
                    onClick={() => setForm({ ...form, mode_of_travel: k })}
                    className={`h-9 text-[11.5px] transition-all ${
                      active
                        ? "border-violet-600 bg-violet-600 text-white font-semibold shadow-md shadow-violet-500/30 ring-2 ring-violet-300 hover:bg-violet-700 hover:text-white"
                        : "border-slate-300 hover:border-violet-400 hover:bg-violet-50"
                    }`}
                    data-testid={`tb-mode-${k}`}
                  >
                    <Icon size={13} className="mr-1.5" /> {k}
                    {active && <span className="ml-1.5 text-[10px] bg-white/20 rounded px-1">✓</span>}
                  </Button>
                );
              })}
            </div>
          </F>

          {!isMulti && (<>
            <F label="From City *"><Input value={form.from_city} onChange={(e) => setForm({ ...form, from_city: e.target.value })} data-testid="tb-from" /></F>
            <F label="To City *"><Input value={form.to_city} onChange={(e) => setForm({ ...form, to_city: e.target.value })} data-testid="tb-to" /></F>
          </>)}
          {isMulti && (
            <F label="Cities (add multiple)" full>
              <CitiesEditor cities={form.cities || []} onChange={(cs) => setForm({ ...form, cities: cs })} />
            </F>
          )}

          <F label="Preferred Travel Date *"><Input type="date" value={form.preferred_travel_date} onChange={(e) => setForm({ ...form, preferred_travel_date: e.target.value })} data-testid="tb-dep-date" /></F>
          <F label="Preferred Departure Time"><Input type="time" value={form.preferred_departure_time} onChange={(e) => setForm({ ...form, preferred_departure_time: e.target.value })} data-testid="tb-dep-time" /></F>
          <F label="Flexible Timing?"><div className="flex items-center gap-2 h-10"><Switch checked={form.flexible_outbound} onCheckedChange={(v) => setForm({ ...form, flexible_outbound: v })} data-testid="tb-dep-flex" /><span className="text-[12px]">{form.flexible_outbound ? "Yes" : "No"}</span></div></F>

          {isRound && (<>
            <F label="Return Date *"><Input type="date" value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} data-testid="tb-ret-date" /></F>
            <F label="Preferred Return Time"><Input type="time" value={form.preferred_return_time} onChange={(e) => setForm({ ...form, preferred_return_time: e.target.value })} data-testid="tb-ret-time" /></F>
            <F label="Flexible Timing?"><div className="flex items-center gap-2 h-10"><Switch checked={form.flexible_return} onCheckedChange={(v) => setForm({ ...form, flexible_return: v })} data-testid="tb-ret-flex" /><span className="text-[12px]">{form.flexible_return ? "Yes" : "No"}</span></div></F>
          </>)}
        </div>

        <div className="flex items-center gap-2 mt-4">
          <Switch checked={form.hotel_required} onCheckedChange={(v) => setForm({ ...form, hotel_required: v })} data-testid="tb-hotel-req" />
          <Label className="text-[12px] font-semibold">Hotel Required?</Label>
        </div>
        {form.hotel_required && (
          <div className="grid grid-cols-2 gap-3 mt-2 border-l-2 border-violet-300 pl-3">
            <F label="Check-in"><Input type="date" value={form.hotel_checkin} onChange={(e) => setForm({ ...form, hotel_checkin: e.target.value })} data-testid="tb-h-in" /></F>
            <F label="Check-out"><Input type="date" value={form.hotel_checkout} onChange={(e) => setForm({ ...form, hotel_checkout: e.target.value })} data-testid="tb-h-out" /></F>
            <F label="No. of Days"><Input value={form.hotel_no_of_days} disabled className="bg-slate-50 mono" data-testid="tb-h-days" /></F>
            <F label="Preferred Hotel Category">
              <select value={form.hotel_category} onChange={(e) => setForm({ ...form, hotel_category: e.target.value })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="tb-h-cat">
                <option value="">—</option>{HOTEL_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Special Requirements" full><Textarea rows={2} value={form.hotel_special_requirements} onChange={(e) => setForm({ ...form, hotel_special_requirements: e.target.value })} data-testid="tb-h-req" /></F>
          </div>
        )}

        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700 mt-4 mb-1">Costs & Assignment</div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Approx Cost of Travel (₹)"><Input value={form.approx_cost_travel} onChange={(e) => setForm({ ...form, approx_cost_travel: e.target.value })} data-testid="tb-cost-travel" /></F>
          <F label="Approx Cost of Hotel (₹)"><Input value={form.approx_cost_hotel} onChange={(e) => setForm({ ...form, approx_cost_hotel: e.target.value })} data-testid="tb-cost-hotel" /></F>
          <F label="Who Assigned this Travel to You *" full>
            <select value={form.assigned_to_id} onChange={(e) => {
              const p = people.find((x) => x.id === e.target.value);
              setForm({ ...form, assigned_to_id: e.target.value, assigned_to_name: p?.name || "" });
            }} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="tb-assigned">
              <option value="">—</option>
              {people.filter((p) => !p.is_temp_account).map((p) => <option key={p.id} value={p.id}>{p.name} · {p.role}</option>)}
            </select>
          </F>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="tb-submit">{editing ? "Save Changes" : "Submit Booking"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CitiesEditor({ cities, onChange }) {
  const [city, setCity] = useState("");
  return (
    <div>
      <div className="flex gap-2">
        <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Add a city" className="flex-1" data-testid="tb-city-input" />
        <Button type="button" onClick={() => { if (city.trim()) { onChange([...cities, city.trim()]); setCity(""); } }} className="bg-violet-600 text-white" data-testid="tb-city-add">Add</Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {cities.map((c, i) => (
          <Badge key={i} className="bg-violet-100 text-violet-800 hover:bg-violet-100 gap-1">
            {i + 1}. {c}
            <button onClick={() => onChange(cities.filter((_, j) => j !== i))} className="ml-1 hover:text-rose-600">×</button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

/* v109.9.115 — Exported so the top-bar Approval Requests panel can reuse
   the same banner + Approve/Reject flow for Travel Bookings. */
export function ViewDialog({ viewing, setViewing, access, onChanged, onApprove, onReject }) {
  const [details, setDetails] = useState({});
  useEffect(() => { setDetails(viewing || {}); }, [viewing]);
  const autoTotal = useMemo(() => {
    const t = Number(details.actual_cost) || 0;
    const h = Number(details.hotel_actual_cost) || 0;
    return t + h;
  }, [details.actual_cost, details.hotel_actual_cost]);
  if (!viewing) return null;
  const isApproved = viewing.status === "Approved" || viewing.status === "Booked";
  const canBook = access.i_can_book && isApproved;
  const effectiveTotal = (details.total_amount_override !== "" && details.total_amount_override !== undefined && details.total_amount_override !== null)
    ? details.total_amount_override
    : autoTotal;
  const saveDetails = async (markBooked) => {
    // v109.9.117 — Payment Details completeness gate. Only enforced at
    // "Mark Booked" so an in-progress Save Details still works.
    if (markBooked) {
      if (!details.paid_by) { toast.error("Select 'Who Paid for this Ticket'"); return; }
      if (details.paid_by === "Employee (Reimbursement)" && !(details.paid_by_employee_name || "").trim()) {
        toast.error("Employee name is required for Reimbursement"); return;
      }
      if (!(details.payment_payer_name || "").trim()) { toast.error(`${paymentPayerLabel(details.paid_by)} is required`); return; }
      if (!details.payment_mode) { toast.error("Payment Mode is required"); return; }
      if (!(details.payment_reference || "").trim()) { toast.error("Reference / Voucher No. is required"); return; }
      if (!details.payment_date) { toast.error("Paid On date is required"); return; }
      if (details.paid_by === "Other" && !(details.paid_remark || "").trim()) {
        toast.error("Notes are mandatory when 'Other' is chosen"); return;
      }
    }
    try {
      const payload = {
        pnr: details.pnr, vendor: details.vendor, booking_reference: details.booking_reference,
        actual_cost: details.actual_cost, ticket_url: details.ticket_url,
        hotel_booking_reference: details.hotel_booking_reference, hotel_actual_cost: details.hotel_actual_cost,
        booking_remark: details.booking_remark,
        depart_actual_date: details.depart_actual_date, depart_actual_time: details.depart_actual_time,
        return_actual_date: details.return_actual_date, return_actual_time: details.return_actual_time,
        paid_by: details.paid_by, paid_by_employee_name: details.paid_by_employee_name, total_amount_override: details.total_amount_override, paid_remark: details.paid_remark,
        // v109.9.117 — Contextual payment details (auditable via change_logs).
        payment_payer_name: details.payment_payer_name, payment_mode: details.payment_mode,
        payment_reference: details.payment_reference, payment_date: details.payment_date,
        mark_booked: !!markBooked,
      };
      await api.patch(`/travel-bookings/${viewing.id}/booking-details`, payload);
      toast.success(markBooked ? "Marked Booked" : "Saved");
      setViewing(null); onChanged?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="tb-view-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">Travel Booking · <Badge className={STATUS_COLOR[viewing.status]}>{viewing.status}</Badge></DialogTitle>
          <div className="text-[11px] text-slate-500">{viewing.created_by_name} · {viewing.created_by_department}</div>
        </DialogHeader>
        <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40 text-[12px] mt-2" data-testid="tb-view-booker">
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700 mb-1.5">Filled By</div>
          <div className="grid grid-cols-2 gap-y-1 gap-x-3">
            <div><b>Name:</b> {viewing.booker_name || viewing.created_by_name || "—"}</div>
            <div><b>Employee ID:</b> {viewing.booker_employee_id || "—"}</div>
            <div><b>Department:</b> {viewing.booker_department || viewing.created_by_department || "—"}</div>
            <div><b>Designation:</b> {viewing.booker_designation || "—"}</div>
            <div><b>Mobile:</b> {viewing.booker_mobile || "—"}</div>
            <div><b>Email:</b> {viewing.booker_email || "—"}</div>
          </div>
        </div>

        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700 mt-3 mb-1">Traveler Information</div>
        <div className="grid grid-cols-2 gap-3 text-[12.5px]">
          <KV k="For Self" v={viewing.travel_for_self ? "Yes" : "No"} />
          <KV k="Full Name (Govt ID)" v={viewing.traveler_full_name} />
          <KV k="Gender" v={viewing.traveler_gender} />
          <KV k="Date of Birth" v={viewing.traveler_dob} />
          <KV k="Age" v={viewing.traveler_age} />
          <KV k="Nationality" v={viewing.traveler_nationality} />
          <KV k="Govt ID Type" v={viewing.traveler_govt_id_type} />
          <KV k="Govt ID Number" v={viewing.traveler_govt_id_number} />
        </div>

        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700 mt-3 mb-1">Trip</div>
        <div className="grid grid-cols-2 gap-3 text-[12.5px]">
          <KV k="Purpose" v={`${viewing.purpose || ""}${viewing.purpose === "Other" && viewing.purpose_other ? ` · ${viewing.purpose_other}` : ""}`} />
          <KV k="Travel Type" v={viewing.travel_type} />
          <KV k="Mode of Travel" v={viewing.mode_of_travel} />
          <KV k="Route" v={viewing.travel_type === "Multi-City" ? (viewing.cities || []).join(" → ") : `${viewing.from_city || ""} → ${viewing.to_city || ""}`} full />
          <KV k="Preferred Travel Date" v={viewing.preferred_travel_date} />
          <KV k="Preferred Departure Time" v={viewing.preferred_departure_time} />
          <KV k="Flexible Outbound" v={viewing.flexible_outbound ? "Yes" : "No"} />
          {viewing.travel_type === "Round Trip" && <>
            <KV k="Return Date" v={viewing.return_date} />
            <KV k="Preferred Return Time" v={viewing.preferred_return_time} />
            <KV k="Flexible Return" v={viewing.flexible_return ? "Yes" : "No"} />
          </>}
        </div>

        {viewing.hotel_required && (
          <>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700 mt-3 mb-1">Hotel</div>
            <div className="grid grid-cols-2 gap-3 text-[12.5px]">
              <KV k="Check-in" v={viewing.hotel_checkin} />
              <KV k="Check-out" v={viewing.hotel_checkout} />
              <KV k="No. of Days" v={viewing.hotel_no_of_days} />
              <KV k="Hotel Category" v={viewing.hotel_category} />
              <KV k="Special Requirements" v={viewing.hotel_special_requirements} full />
            </div>
          </>
        )}

        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700 mt-3 mb-1">Costs & Assignment</div>
        <div className="grid grid-cols-2 gap-3 text-[12.5px]">
          <KV k="Approx Cost of Travel (₹)" v={viewing.approx_cost_travel} />
          <KV k="Approx Cost of Hotel (₹)" v={viewing.approx_cost_hotel} />
          <KV k="Assigned By" v={viewing.assigned_to_name} full />
        </div>

        <div className="text-[12.5px]">
          {viewing.rejection_remark && <div className="border border-rose-200 bg-rose-50 rounded-md p-2.5 mt-3"><div className="text-[10px] uppercase font-semibold text-rose-700">Rejected</div><div>{viewing.rejection_remark}</div></div>}
          {viewing.approval_remark && <div className="border border-emerald-200 bg-emerald-50 rounded-md p-2.5 mt-3"><div className="text-[10px] uppercase font-semibold text-emerald-700">Approval Note</div><div>{viewing.approval_remark}</div></div>}
        </div>

        {(viewing.pnr || viewing.vendor || viewing.booking_reference || viewing.actual_cost || viewing.paid_by) && !canBook && (
          <>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-blue-700 mt-3 mb-1">Ticket Booking Details</div>
            <div className="grid grid-cols-2 gap-3 text-[12.5px]">
              <KV k="PNR" v={viewing.pnr} />
              <KV k="Vendor" v={viewing.vendor} />
              <KV k="Booking Reference" v={viewing.booking_reference} />
              <KV k="Actual Travel Cost (₹)" v={viewing.actual_cost} />
              {viewing.hotel_required && <KV k="Hotel Booking Ref" v={viewing.hotel_booking_reference} />}
              {viewing.hotel_required && <KV k="Hotel Actual Cost (₹)" v={viewing.hotel_actual_cost} />}
              <KV k="Ticket URL" v={viewing.ticket_url ? <a href={viewing.ticket_url} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{viewing.ticket_url}</a> : ""} full />
              <KV k="Booking Remark" v={viewing.booking_remark} full />
              <KV k="Who Paid for this Ticket" v={viewing.paid_by === "Employee (Reimbursement)" && viewing.paid_by_employee_name ? `Employee (Reimbursement) · ${viewing.paid_by_employee_name}` : viewing.paid_by} />
              {viewing.payment_payer_name && <KV k={paymentPayerLabel(viewing.paid_by)} v={viewing.payment_payer_name} />}
              {viewing.payment_mode && <KV k="Payment Mode" v={viewing.payment_mode} />}
              {viewing.payment_reference && <KV k="Reference / Voucher No." v={viewing.payment_reference} />}
              {viewing.payment_date && <KV k="Paid On" v={viewing.payment_date} />}
              <KV k="Total Amount (₹)" v={
                (viewing.total_amount_override !== "" && viewing.total_amount_override !== undefined && viewing.total_amount_override !== null)
                  ? `${viewing.total_amount_override} (override)`
                  : ((Number(viewing.actual_cost) || 0) + (Number(viewing.hotel_actual_cost) || 0)) || ""
              } />
              <KV k="Paid Remark" v={viewing.paid_remark} full />
            </div>
          </>
        )}

        {viewing.reimbursement_status && (
          <>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-orange-700 mt-3 mb-1">Reimbursement</div>
            <div className="grid grid-cols-2 gap-3 text-[12.5px]">
              <KV k="Reimbursement Status" v={viewing.reimbursement_status} />
              <KV k="Total Amount (₹)" v={tbTotal(viewing) || ""} />
              <KV k="Required?" v={viewing.reimbursement_required === true ? "Yes" : (viewing.reimbursement_required === false ? "No" : "—")} />
              <KV k="Decision By" v={viewing.reimbursement_required_by_name} />
              {viewing.reimbursement_required_remark && <KV k="Decision Remark" v={viewing.reimbursement_required_remark} full />}
              {viewing.ledger_entry_done && <KV k="Ledger Entry By" v={viewing.ledger_entry_by_name} />}
              {viewing.ledger_entry_done && <KV k="Ledger Entry At" v={viewing.ledger_entry_at ? viewing.ledger_entry_at.slice(0, 10) : ""} />}
              {viewing.ledger_remark && <KV k="Ledger Remark" v={viewing.ledger_remark} full />}
              {viewing.reimbursement_paid && <KV k="Paid Via" v={`${viewing.reimbursement_payment_mode || ""} · ${viewing.reimbursement_reference_number || ""}`} />}
              {viewing.reimbursement_paid && <KV k="Paid Amount (₹)" v={viewing.reimbursement_paid_amount} />}
              {viewing.reimbursement_paid && <KV k="Paid By (Master)" v={viewing.reimbursement_paid_by_name} />}
              {viewing.reimbursement_paid && <KV k="Paid At" v={viewing.reimbursement_paid_at ? viewing.reimbursement_paid_at.slice(0, 10) : ""} />}
              {viewing.reimbursement_paid_remark && <KV k="Payment Remark" v={viewing.reimbursement_paid_remark} full />}
            </div>
          </>
        )}

        {canBook && (
          <div className="border-t pt-3 mt-3">
            <div className="text-[10.5px] uppercase font-semibold text-blue-700 mb-2">Ticket Booking Details</div>
            <div className="grid grid-cols-2 gap-3">
              <F label="PNR"><Input value={details.pnr || ""} onChange={(e) => setDetails({ ...details, pnr: e.target.value })} className="mono" data-testid="tb-d-pnr" /></F>
              <F label="Vendor"><Input value={details.vendor || ""} onChange={(e) => setDetails({ ...details, vendor: e.target.value })} placeholder="IRCTC / MakeMyTrip / IndiGo" data-testid="tb-d-vendor" /></F>
              <F label="Booking Reference"><Input value={details.booking_reference || ""} onChange={(e) => setDetails({ ...details, booking_reference: e.target.value })} className="mono" data-testid="tb-d-ref" /></F>
              <F label="Actual Cost (₹)"><Input value={details.actual_cost || ""} onChange={(e) => setDetails({ ...details, actual_cost: e.target.value })} data-testid="tb-d-cost" /></F>
              <F label="Ticket URL / E-Ticket" full><Input value={details.ticket_url || ""} onChange={(e) => setDetails({ ...details, ticket_url: e.target.value })} placeholder="https://…" data-testid="tb-d-url" /></F>
              {viewing.hotel_required && (<>
                <F label="Hotel Booking Ref"><Input value={details.hotel_booking_reference || ""} onChange={(e) => setDetails({ ...details, hotel_booking_reference: e.target.value })} data-testid="tb-d-hotel-ref" /></F>
                <F label="Hotel Actual Cost (₹)"><Input value={details.hotel_actual_cost || ""} onChange={(e) => setDetails({ ...details, hotel_actual_cost: e.target.value })} data-testid="tb-d-hotel-cost" /></F>
              </>)}
              <F label="Booking Remark" full><Textarea rows={2} value={details.booking_remark || ""} onChange={(e) => setDetails({ ...details, booking_remark: e.target.value })} data-testid="tb-d-remark" /></F>
              <F label="Who Paid for this Ticket *">
                <select value={details.paid_by || ""} onChange={(e) => setDetails({ ...details, paid_by: e.target.value, paid_by_employee_name: e.target.value === "Employee (Reimbursement)" ? (details.paid_by_employee_name || "") : "" })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="tb-d-paidby">
                  <option value="">—</option>
                  <option>Company</option>
                  <option>Employee (Reimbursement)</option>
                  <option>Client</option>
                  <option>Vendor</option>
                  <option>Other</option>
                </select>
              </F>
              {details.paid_by === "Employee (Reimbursement)" && (
                <F label="Name of Employee Who Paid *">
                  <Input value={details.paid_by_employee_name || ""} onChange={(e) => setDetails({ ...details, paid_by_employee_name: e.target.value })} placeholder="Type the employee's full name" data-testid="tb-d-paidby-emp" />
                </F>
              )}
              <F label={`Total Amount (Auto ₹${autoTotal}) — Override`}>
                <Input value={details.total_amount_override ?? ""} onChange={(e) => setDetails({ ...details, total_amount_override: e.target.value })} placeholder={`Leave blank to use ₹${autoTotal}`} className="mono" data-testid="tb-d-total-override" />
                <div className="text-[10.5px] text-slate-500 mt-1">Final billed: <b className="text-slate-700 mono">₹{effectiveTotal || 0}</b></div>
              </F>
              {/* v109.9.117 — Contextual Payment Details panel — appears as soon
                  as a "Who Paid" option is picked. Every field is mandatory
                  before Mark Booked. */}
              {details.paid_by && (
                <div className="col-span-2 mt-1 rounded-md border-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50/40 dark:bg-indigo-950/20 p-3">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-indigo-800 dark:text-indigo-200 mb-2 flex items-center gap-1.5">
                    <Wallet size={12} /> Payment Details — {details.paid_by}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <F label={paymentPayerLabel(details.paid_by) + " *"}>
                      <Input
                        value={details.payment_payer_name || ""}
                        onChange={(e) => setDetails({ ...details, payment_payer_name: e.target.value })}
                        placeholder={paymentPayerPlaceholder(details.paid_by)}
                        data-testid="tb-d-pd-payer"
                      />
                    </F>
                    <F label="Payment Mode *">
                      <select
                        value={details.payment_mode || ""}
                        onChange={(e) => setDetails({ ...details, payment_mode: e.target.value })}
                        className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white"
                        data-testid="tb-d-pd-mode"
                      >
                        <option value="">—</option>
                        <option>NEFT</option>
                        <option>RTGS</option>
                        <option>UPI</option>
                        <option>Card</option>
                        <option>Cash</option>
                        <option>Cheque</option>
                        <option>Voucher</option>
                        <option>Corporate Card</option>
                        <option>Other</option>
                      </select>
                    </F>
                    <F label="Reference / Voucher No. *">
                      <Input
                        value={details.payment_reference || ""}
                        onChange={(e) => setDetails({ ...details, payment_reference: e.target.value })}
                        placeholder="Txn ID · Voucher No · Card last-4 …"
                        data-testid="tb-d-pd-ref"
                      />
                    </F>
                    <F label="Paid On *">
                      <Input
                        type="date"
                        value={details.payment_date || ""}
                        onChange={(e) => setDetails({ ...details, payment_date: e.target.value })}
                        data-testid="tb-d-pd-date"
                      />
                    </F>
                    <F label={details.paid_by === "Other" ? "Describe · Notes *" : "Notes"} full>
                      <Textarea
                        rows={2}
                        value={details.paid_remark || ""}
                        onChange={(e) => setDetails({ ...details, paid_remark: e.target.value })}
                        placeholder={details.paid_by === "Other" ? "Explain who paid + supporting reference (mandatory)" : "Optional context — reconciliation notes, approver etc."}
                        data-testid="tb-d-paidremark"
                      />
                    </F>
                  </div>
                  {details.paid_by === "Employee (Reimbursement)" && (
                    <div className="mt-2 text-[10.5px] text-amber-900 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5">
                      This booking will land in the <b>Reimbursement</b> tab after Mark Booked so Accounts can pay the employee back.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* v109.9.116 — Change history: every ticket-detail edit + who did it. */}
        {viewing.change_logs?.length > 0 && (
          <div className="mt-3">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Change History</div>
            <div className="space-y-1.5">
              {viewing.change_logs.slice().reverse().map((log, i) => {
                const at = log.at ? new Date(log.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true }) : "";
                return (
                  <div key={i} className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40 p-2 text-[11.5px]" data-testid={`tb-changelog-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold text-slate-800 dark:text-slate-100">
                        {log.by_name || "—"}
                        <span className="ml-1.5 text-[10.5px] font-normal text-slate-500">
                          · {log.action === "book_ticket" ? "marked Booked" : "edited ticket details"}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500">{at}</div>
                    </div>
                    <div className="pl-2 border-l-2 border-slate-300 dark:border-slate-600 space-y-0.5">
                      {(log.changes || []).map((c, j) => (
                        <div key={j} className="text-[10.5px]">
                          <b className="text-slate-700 dark:text-slate-200">{c.field}:</b>{" "}
                          <span className="text-rose-700 dark:text-rose-400 line-through mono">{String(c.old || "—")}</span>{" "}
                          <span className="text-slate-400">→</span>{" "}
                          <span className="text-emerald-700 dark:text-emerald-400 mono">{String(c.new || "—")}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {viewing.__for_approval && viewing.status === "Submitted" && (
            <>
              <Button onClick={() => onReject?.(viewing)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="tb-view-reject">
                <XCircle size={13} className="mr-1.5" /> Reject
              </Button>
              <Button onClick={() => onApprove?.(viewing)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tb-view-approve">
                <CheckCircle2 size={13} className="mr-1.5" /> Approve
              </Button>
            </>
          )}
          {canBook && <>
            <Button variant="outline" onClick={() => saveDetails(false)} data-testid="tb-d-save">Save Details</Button>
            <Button onClick={() => saveDetails(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="tb-d-book">Mark Booked</Button>
          </>}
          <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
const KV = ({ k, v, full }) => <div className={full ? "col-span-2" : ""}><div className="text-[10px] uppercase text-slate-500 font-semibold">{k}</div><div className="text-slate-800 break-words mt-0.5">{v || "—"}</div></div>;

function TabAccessDialog({ open, setOpen, people, initial, onSaved }) {
  const [approval, setApproval] = useState(() => new Set(initial.approval_user_ids || []));
  const [ticket, setTicket] = useState(() => new Set(initial.ticket_user_ids || []));
  const [reimb, setReimb] = useState(() => new Set(initial.reimbursement_user_ids || []));
  const [search, setSearch] = useState("");
  useEffect(() => {
    setApproval(new Set(initial.approval_user_ids || []));
    setTicket(new Set(initial.ticket_user_ids || []));
    setReimb(new Set(initial.reimbursement_user_ids || []));
  }, [initial]);
  const toggle = (set, setSet, id) => { const s = new Set(set); if (s.has(id)) { s.delete(id); } else { s.add(id); } setSet(s); };
  const save = async () => {
    try {
      await api.post("/travel-bookings/tab-access", {
        approval_user_ids: Array.from(approval),
        ticket_user_ids: Array.from(ticket),
        reimbursement_user_ids: Array.from(reimb),
      });
      toast.success("Tab access saved"); setOpen(false); onSaved?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const filtered = (people || []).filter((p) => !p.is_temp_account && (!search || [p.name, p.email, p.role].some((v) => (v || "").toLowerCase().includes(search.toLowerCase()))));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="tb-access-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Travel Booking · Tab Access</DialogTitle>
          <div className="text-[11px] text-slate-500">Pick which employees can see the Approval, Ticket Booking and Reimbursement tabs.</div>
        </DialogHeader>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="h-9 text-xs mb-2" data-testid="tb-access-search" />
        <div className="border rounded-md max-h-[400px] overflow-y-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 sticky top-0"><tr><th className="text-left px-3 py-2">Employee</th><th className="text-center px-3 py-2">Approval</th><th className="text-center px-3 py-2">Ticket Booking</th><th className="text-center px-3 py-2">Reimbursement</th></tr></thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t" data-testid={`tb-access-row-${p.id}`}>
                  <td className="px-3 py-2"><div className="font-semibold">{p.name}</div><div className="text-[10.5px] text-slate-500">{p.email} · {p.role}</div></td>
                  <td className="px-3 py-2 text-center"><input type="checkbox" checked={approval.has(p.id)} onChange={() => toggle(approval, setApproval, p.id)} data-testid={`tb-access-approval-${p.id}`} /></td>
                  <td className="px-3 py-2 text-center"><input type="checkbox" checked={ticket.has(p.id)} onChange={() => toggle(ticket, setTicket, p.id)} data-testid={`tb-access-ticket-${p.id}`} /></td>
                  <td className="px-3 py-2 text-center"><input type="checkbox" checked={reimb.has(p.id)} onChange={() => toggle(reimb, setReimb, p.id)} data-testid={`tb-access-reimb-${p.id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="tb-access-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
